import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import useSWR, { mutate as mutateSWR } from "swr";

import {
  deleteBench,
  deleteBenchRoom,
  deleteComplex,
  deleteCourtRoom,
  deleteDistrict,
  deleteEstablishment,
  deleteHighCourt,
} from "../../../api/courtApis";
import { AddUpdateBenche } from "./highcourt/AddUpdateBenche";
import { AddUpdateCourtHall } from "./highcourt/AddUpdateCourtHall";
import { AddUpdateHighCourt } from "./highcourt/AddUpdateHighCourt";
import { Benches } from "./highcourt/Benches";
import { CourtHalls } from "./highcourt/CourtHalls";
import { CourtsHome } from "./CourtsHome";
import { HighCourts } from "./highcourt/HighCourts";
import {
  ComplexModal,
  CourtRoomModal,
  DistrictModal,
  EstablishmentModal,
} from "./districtcourt/Modals";
import { CourtComplexes } from "./districtcourt/CourtComplexes";
import { CourtEstablishments } from "./districtcourt/CourtEstablishments";
import { DistrictCourtRooms } from "./districtcourt/DistrictCourtRooms";
import { DistrictCourts } from "./districtcourt/DistrictCourts";
import { ConfirmDeleteModal } from "./highcourt/ModalShell";
import type {
  CourtComplexRow,
  CourtRoomRow,
  DistrictRow,
  EstablishmentRow,
} from "./districtcourt/types";
import type {
  BenchRoomRow,
  BenchRow,
  DeleteTarget,
  HighCourtRow,
  ViewMode,
} from "./highcourt/types";

const highCourtsKey = "/api/court/get-highcourt";
const getBenchesKey = (highCourtId: string) =>
  `/api/court/get-benches/${highCourtId}?page=1&limit=1000`;
const getBenchRoomsKey = (benchId: string) =>
  `/api/court/get-bench-rooms/${benchId}?page=1&limit=1000`;
const getDistrictsKey = "/api/court/get-districts?page=1&limit=1000";
const districtCourtRowsKey = "new-courts-district-court-rows";
const benchRoomCountKeyPrefix = "new-courts-bench-room-counts:";
const complexStatsKeyPrefix = "new-courts-complex-stats:";
const establishmentRoomCountKeyPrefix = "new-courts-establishment-room-counts:";

const revalidateCacheByPrefix = (prefix: string) =>
  mutateSWR(
    (key) => typeof key === "string" && key.startsWith(prefix),
    undefined,
    { revalidate: true },
  );

const refreshBenchRoomDependentData = async () => {
  await revalidateCacheByPrefix(benchRoomCountKeyPrefix);
};

const refreshDistrictDependentData = async () => {
  await Promise.all([
    mutateSWR(districtCourtRowsKey),
    revalidateCacheByPrefix(complexStatsKeyPrefix),
    revalidateCacheByPrefix(establishmentRoomCountKeyPrefix),
  ]);
};

const NewCourts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get("view") as ViewMode) || "home";
  const highCourtId = searchParams.get("court") || "";
  const benchId = searchParams.get("bench") || "";
  const districtId = searchParams.get("district") || "";
  const complexId = searchParams.get("complex") || "";
  const establishmentId = searchParams.get("establishment") || "";
  const [searchTerm, setSearchTerm] = useState("");
  const [highCourtModal, setHighCourtModal] = useState<HighCourtRow | null>();
  const [benchModal, setBenchModal] = useState<BenchRow | null>();
  const [benchRoomModal, setBenchRoomModal] = useState<BenchRoomRow | null>();
  const [activeHighCourt, setActiveHighCourt] = useState<HighCourtRow | null>(
    null,
  );
  const [activeBench, setActiveBench] = useState<BenchRow | null>(null);
  const [districtModal, setDistrictModal] = useState<DistrictRow | null>();
  const [complexModal, setComplexModal] = useState<CourtComplexRow | null>();
  const [establishmentModal, setEstablishmentModal] =
    useState<EstablishmentRow | null>();
  const [courtRoomModal, setCourtRoomModal] = useState<CourtRoomRow | null>();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [loading, setLoading] = useState(false);

  const shouldLoadDistricts = [
    "complexes",
    "establishments",
    "court-rooms",
  ].includes(view);
  const { data: districtResponse, mutate: mutateDistricts } = useSWR(
    shouldLoadDistricts ? getDistrictsKey : null,
  );
  const districts = ((districtResponse?.status === false
    ? []
    : districtResponse?.data) || []) as DistrictRow[];
  const selectedDistrict = districts.find(
    (district) => district._id === districtId,
  );

  const {
    data: complexResponse,
    mutate: mutateComplexes,
    isLoading: complexesLoading,
  } = useSWR(
    shouldLoadDistricts && districtId
      ? `/api/court/get-complexes/${districtId}`
      : null,
  );
  const complexes = ((complexResponse?.status === false
    ? []
    : complexResponse?.data) || []) as CourtComplexRow[];

  const selectedComplex = complexes.find(
    (complex) => complex._id === complexId,
  );

  const {
    data: establishmentResponse,
    mutate: mutateEstablishments,
    isLoading: establishmentsLoading,
  } = useSWR(
    shouldLoadDistricts && complexId
      ? `/api/court/get-establishments/${complexId}`
      : null,
  );
  const establishments = ((establishmentResponse?.status === false
    ? []
    : establishmentResponse?.data) || []) as EstablishmentRow[];
  const selectedEstablishment = establishments.find(
    (establishment) => establishment._id === establishmentId,
  );

  const courtRoomsParentId = selectedComplex?.courtEstablishment
    ? establishmentId
    : complexId;
  const {
    data: courtRoomResponse,
    mutate: mutateCourtRooms,
    isLoading: courtRoomsLoading,
  } = useSWR(
    shouldLoadDistricts && courtRoomsParentId
      ? `/api/court/get-courtrooms/${courtRoomsParentId}`
      : null,
  );
  const courtRooms = ((courtRoomResponse?.status === false
    ? []
    : courtRoomResponse?.data) || []) as CourtRoomRow[];

  const goHome = () => {
    setSearchParams({});
    setSearchTerm("");
  };

  const goHighCourts = () => {
    setSearchParams({ view: "high-courts" });
    setSearchTerm("");
  };

  const goDistrictCourts = () => {
    setSearchParams({ view: "district-courts" });
    setSearchTerm("");
  };

  const goBenches = (court: HighCourtRow) => {
    setSearchParams({ view: "benches", court: court._id });
  };

  const goCourtHalls = (bench: BenchRow) => {
    if (!highCourtId) return;
    setSearchParams({
      view: "court-halls",
      court: highCourtId,
      bench: bench._id,
    });
  };

  const goComplexes = (district: DistrictRow) => {
    setSearchParams({
      view: "complexes",
      district: district._id,
    });
  };

  const goAfterComplex = (complex: CourtComplexRow) => {
    if (!selectedDistrict) return;

    setSearchParams({
      view: complex.courtEstablishment ? "establishments" : "court-rooms",
      district: selectedDistrict._id,
      complex: complex._id,
    });
  };

  const goCourtRooms = (establishment: EstablishmentRow) => {
    if (!selectedDistrict || !selectedComplex) return;

    setSearchParams({
      view: "court-rooms",
      district: selectedDistrict._id,
      complex: selectedComplex._id,
      establishment: establishment._id,
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setLoading(true);

    try {
      if (deleteTarget.type === "highCourt") {
        await deleteHighCourt(deleteTarget.id);
        await mutateSWR(highCourtsKey);
        goHighCourts();
      }

      if (deleteTarget.type === "bench") {
        await deleteBench(deleteTarget.id);
        await mutateSWR(getBenchesKey(highCourtId));
      }

      if (deleteTarget.type === "benchRoom") {
        await deleteBenchRoom(deleteTarget.id);
        await mutateSWR(getBenchRoomsKey(benchId));
        await refreshBenchRoomDependentData();
      }

      if (deleteTarget.type === "district") {
        await deleteDistrict(deleteTarget.id);
        await mutateDistricts();
        await mutateSWR(getDistrictsKey);
        await mutateSWR(districtCourtRowsKey);
      }

      if (deleteTarget.type === "complex") {
        await deleteComplex(deleteTarget.id);
        await mutateComplexes();
        await refreshDistrictDependentData();
      }

      if (deleteTarget.type === "establishment") {
        await deleteEstablishment(deleteTarget.id);
        await mutateEstablishments();
        await refreshDistrictDependentData();
      }

      if (deleteTarget.type === "courtRoom") {
        await deleteCourtRoom(deleteTarget.id);
        await mutateCourtRooms();
        await refreshDistrictDependentData();
      }

      toast.success(`${deleteTarget.label} deleted successfully`);
      setDeleteTarget(null);
    } catch (error: any) {
      toast.error(error?.message || `Failed to delete ${deleteTarget.label}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-100px)] overflow-y-auto p-4 bg-[#eef4ff] text-slate-950">
      {view === "home" && (
        <CourtsHome
          onHighCourts={goHighCourts}
          onDistrictCourts={goDistrictCourts}
        />
      )}

      {view === "high-courts" && (
        <HighCourts
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onBack={goHome}
          onAdd={() => setHighCourtModal(null)}
          onOpen={goBenches}
          onEdit={(court) => setHighCourtModal(court)}
          onDelete={(court) =>
            setDeleteTarget({
              type: "highCourt",
              id: court._id,
              label: "High Court",
            })
          }
          loading={loading}
        />
      )}

      {view === "district-courts" && (
        <DistrictCourts
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onBack={goHome}
          onAdd={() => setDistrictModal(null)}
          onOpen={goComplexes}
          onEdit={(district) => setDistrictModal(district)}
          onDelete={(district) =>
            setDeleteTarget({
              type: "district",
              id: district._id,
              label: "District",
            })
          }
        />
      )}

      {view === "complexes" && selectedDistrict && (
        <CourtComplexes
          district={selectedDistrict}
          complexes={complexes}
          isLoading={complexesLoading}
          onBack={goDistrictCourts}
          onAdd={() => setComplexModal(null)}
          onOpen={goAfterComplex}
          onEdit={(complex) => setComplexModal(complex)}
          onDelete={(complex) =>
            setDeleteTarget({
              type: "complex",
              id: complex._id,
              label: "Court Complex",
            })
          }
        />
      )}

      {view === "establishments" && selectedDistrict && selectedComplex && (
        <CourtEstablishments
          district={selectedDistrict}
          complex={selectedComplex}
          establishments={establishments}
          isLoading={establishmentsLoading}
          onBack={() => goComplexes(selectedDistrict)}
          onAdd={() => setEstablishmentModal(null)}
          onOpen={goCourtRooms}
          onEdit={(establishment) => setEstablishmentModal(establishment)}
          onDelete={(establishment) =>
            setDeleteTarget({
              type: "establishment",
              id: establishment._id,
              label: "Court Establishment",
            })
          }
        />
      )}

      {view === "court-rooms" && selectedDistrict && selectedComplex && (
        <DistrictCourtRooms
          title={selectedEstablishment?.name || selectedComplex.complexName}
          complex={selectedComplex}
          establishment={selectedEstablishment}
          rooms={courtRooms}
          isLoading={courtRoomsLoading}
          onBack={() =>
            selectedComplex.courtEstablishment
              ? goAfterComplex(selectedComplex)
              : goComplexes(selectedDistrict)
          }
          onAdd={() => setCourtRoomModal(null)}
          onEdit={(room) => setCourtRoomModal(room)}
          onDelete={(room) =>
            setDeleteTarget({
              type: "courtRoom",
              id: room._id,
              label: "Court Room",
            })
          }
        />
      )}

      {view === "benches" && highCourtId && (
        <Benches
          highCourtId={highCourtId}
          onBack={goHighCourts}
          onAdd={(court) => {
            setActiveHighCourt(court);
            setBenchModal(null);
          }}
          onOpen={goCourtHalls}
          onEdit={(bench, court) => {
            setActiveHighCourt(court);
            setBenchModal(bench);
          }}
          onDelete={(bench) =>
            setDeleteTarget({ type: "bench", id: bench._id, label: "Bench" })
          }
        />
      )}

      {view === "court-halls" && highCourtId && benchId && (
        <CourtHalls
          highCourtId={highCourtId}
          benchId={benchId}
          onBack={() =>
            setSearchParams({ view: "benches", court: highCourtId })
          }
          onAdd={(court, bench) => {
            setActiveHighCourt(court);
            setActiveBench(bench);
            setBenchRoomModal(null);
          }}
          onEdit={(room, court, bench) => {
            setActiveHighCourt(court);
            setActiveBench(bench);
            setBenchRoomModal(room);
          }}
          onDelete={(room) =>
            setDeleteTarget({
              type: "benchRoom",
              id: room._id,
              label: "Court Hall",
            })
          }
        />
      )}

      {highCourtModal !== undefined && (
        <AddUpdateHighCourt
          court={highCourtModal}
          onClose={() => setHighCourtModal(undefined)}
          onSaved={async () => {
            await mutateSWR(highCourtsKey);
            setHighCourtModal(undefined);
          }}
        />
      )}

      {benchModal !== undefined && activeHighCourt && (
        <AddUpdateBenche
          highCourt={activeHighCourt}
          bench={benchModal}
          onClose={() => {
            setBenchModal(undefined);
            setActiveHighCourt(null);
          }}
          onSaved={async () => {
            await mutateSWR(getBenchesKey(activeHighCourt._id));
            setBenchModal(undefined);
            setActiveHighCourt(null);
          }}
        />
      )}

      {benchRoomModal !== undefined && activeHighCourt && activeBench && (
        <AddUpdateCourtHall
          highCourt={activeHighCourt}
          bench={activeBench}
          room={benchRoomModal}
          onClose={() => {
            setBenchRoomModal(undefined);
            setActiveHighCourt(null);
            setActiveBench(null);
          }}
          onSaved={async () => {
            await mutateSWR(getBenchRoomsKey(activeBench._id));
            await refreshBenchRoomDependentData();
            setBenchRoomModal(undefined);
            setActiveHighCourt(null);
            setActiveBench(null);
          }}
        />
      )}

      {districtModal !== undefined && (
        <DistrictModal
          district={districtModal}
          onClose={() => setDistrictModal(undefined)}
          onSaved={async () => {
            await mutateDistricts();
            await mutateSWR(getDistrictsKey);
            await mutateSWR(districtCourtRowsKey);
            setDistrictModal(undefined);
          }}
        />
      )}

      {complexModal !== undefined && selectedDistrict && (
        <ComplexModal
          district={selectedDistrict}
          complex={complexModal}
          onClose={() => setComplexModal(undefined)}
          onSaved={async () => {
            await mutateComplexes();
            await refreshDistrictDependentData();
            setComplexModal(undefined);
          }}
        />
      )}

      {establishmentModal !== undefined &&
        selectedDistrict &&
        selectedComplex && (
          <EstablishmentModal
            district={selectedDistrict}
            complex={selectedComplex}
            establishment={establishmentModal}
            onClose={() => setEstablishmentModal(undefined)}
            onSaved={async () => {
              await mutateEstablishments();
              await refreshDistrictDependentData();
              setEstablishmentModal(undefined);
            }}
          />
        )}

      {courtRoomModal !== undefined && selectedDistrict && selectedComplex && (
        <CourtRoomModal
          district={selectedDistrict}
          complex={selectedComplex}
          establishment={selectedEstablishment}
          room={courtRoomModal}
          onClose={() => setCourtRoomModal(undefined)}
          onSaved={async () => {
            await mutateCourtRooms();
            await refreshDistrictDependentData();
            setCourtRoomModal(undefined);
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteModal
          label={deleteTarget.label}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          loading={loading}
        />
      )}
    </div>
  );
};

export default NewCourts;
